/**
 * Document management API client for the dashboard.
 */
import type { ApiClient } from '../api/client.ts';

export interface DocFolder {
  readonly id: number;
  readonly name: string;
  readonly parent_id: number | null;
  readonly created_by: number | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface DocFile {
  readonly id: number;
  readonly name: string;
  readonly folder_id: number | null;
  readonly s3_key: string;
  readonly mime_type: string;
  readonly size_bytes: number;
  readonly uploaded_by: number | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface FolderContents {
  readonly folders: readonly DocFolder[];
  readonly files: readonly DocFile[];
  readonly breadcrumbs: readonly { readonly id: number; readonly name: string }[];
}

export interface DocumentsApi {
  readonly listFolder: (folderId: number | null) => Promise<FolderContents>;
  readonly createFolder: (
    name: string,
    parentId: number | null,
    csrfToken: string,
  ) => Promise<DocFolder>;
  readonly renameFolder: (id: number, name: string, csrfToken: string) => Promise<DocFolder>;
  readonly deleteFolder: (id: number, csrfToken: string) => Promise<void>;
  readonly uploadFile: (file: File, folderId: number | null, csrfToken: string) => Promise<DocFile>;
  readonly renameFile: (id: number, name: string, csrfToken: string) => Promise<DocFile>;
  readonly deleteFile: (id: number, csrfToken: string) => Promise<void>;
  readonly getDownloadUrl: (id: number) => string;
}

const passthrough = { safeParse: (v: unknown) => ({ success: true as const, data: v }) } as never;

export const createDocumentsApi = (client: ApiClient, baseUrl = ''): DocumentsApi => ({
  listFolder: async (folderId) => {
    const param = folderId !== null ? `?folderId=${String(folderId)}` : '';
    const result = await client.get(`/api/admin/documents${param}`, passthrough);
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as FolderContents;
  },

  createFolder: async (name, parentId, csrfToken) => {
    const result = await client.send('POST', '/api/admin/documents/folders', passthrough, {
      body: { name, parentId },
      csrfToken,
    });
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as DocFolder;
  },

  renameFolder: async (id, name, csrfToken) => {
    const result = await client.send(
      'PATCH',
      `/api/admin/documents/folders/${String(id)}`,
      passthrough,
      {
        body: { name },
        csrfToken,
      },
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as DocFolder;
  },

  deleteFolder: async (id, csrfToken) => {
    const result = await client.send(
      'DELETE',
      `/api/admin/documents/folders/${String(id)}`,
      passthrough,
      {
        csrfToken,
      },
    );
    if (!result.ok) throw new Error(result.failure.message);
  },

  uploadFile: async (file, folderId, csrfToken) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId !== null) formData.append('folderId', String(folderId));
    formData.append('name', file.name);

    const response = await fetch(`${baseUrl}/api/admin/documents/files`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { 'x-csrf-token': csrfToken },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error((body as { error?: { message?: string } }).error?.message ?? 'Upload failed');
    }
    return response.json() as Promise<DocFile>;
  },

  renameFile: async (id, name, csrfToken) => {
    const result = await client.send(
      'PATCH',
      `/api/admin/documents/files/${String(id)}`,
      passthrough,
      {
        body: { name },
        csrfToken,
      },
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as DocFile;
  },

  deleteFile: async (id, csrfToken) => {
    const result = await client.send(
      'DELETE',
      `/api/admin/documents/files/${String(id)}`,
      passthrough,
      {
        csrfToken,
      },
    );
    if (!result.ok) throw new Error(result.failure.message);
  },

  getDownloadUrl: (id) => `${baseUrl}/api/admin/documents/files/${String(id)}/download`,
});
